"use client";

import Image from "@/components/OptimizedImage";
import Link from "next/link";
import { useState } from "react";

import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { BlogPost } from "@/lib/item-reader";
import { cn } from "@/lib/utils";
import { useCookieConsent } from "@lightwaves/hooks";

export interface EnhancedBlogPost extends BlogPost {
  category: string;
  readTime: string;
}

interface BlogClientProps {
  posts: EnhancedBlogPost[];
  heroTitle?: string;
  heroDescription?: string;
  ecommerceEnabled?: boolean;
}

export default function BlogClient({ posts, heroTitle, heroDescription, ecommerceEnabled = false }: BlogClientProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const { hasConsent } = useCookieConsent();
  const postsPerPage = 9;

  // Snipcart mode: ecommerce enabled AND user has given consent
  const snipcartMode = ecommerceEnabled && hasConsent;

  const totalPages = Math.ceil(posts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const currentPosts = posts.slice(startIndex, startIndex + postsPerPage);

  return (
    <section className="section-padding bigger-container">
      <div className="mb-4 text-left">
        <h1 className="mb-4 text-3xl tracking-tight">{heroTitle || "Kunst"}</h1>
        {heroDescription && <p className="text-muted-foreground text-lg max-w-3xl">{heroDescription}</p>}
      </div>

        {/* POSTS GRID: All posts are standard cards */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {currentPosts.map((post) => (
            <div key={post.slug} className="h-full">
              <Link href={`/art/${post.slug}`} className="group block h-full">
                <BlogCard post={post} className="h-full" snipcartMode={snipcartMode} />
              </Link>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-input/50 mt-12 flex justify-center border-t pt-4 md:mt-20">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage > 1) setCurrentPage(currentPage - 1);
                    }}
                    className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                  />
                </PaginationItem>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <PaginationItem key={page}>
                    <PaginationLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setCurrentPage(page);
                      }}
                      isActive={currentPage === page}
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ))}

                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage < totalPages) setCurrentPage(currentPage + 1);
                    }}
                    className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
    </section>
  );
}

function BlogCard({ post, className, snipcartMode }: { post: EnhancedBlogPost; className?: string; snipcartMode: boolean }) {
  const hasValidPrice = (price: string | number | undefined): boolean => {
    if (price === undefined || price === null) return false;
    const priceStr = String(price).trim();
    if (!priceStr || priceStr.toUpperCase() === "TBD") return false;
    return !isNaN(parseFloat(priceStr));
  };

  // Determine if Buy Print indicator should show
  const shouldShowBuyPrint = (): boolean => {
    if (snipcartMode) {
      // In Snipcart mode, check if any print has valid price
      return post.prints?.some(print => hasValidPrice(print.price)) ?? false;
    } else {
      // Not in Snipcart mode, check if shopLink exists
      return !!post.shopLink;
    }
  };

  return (
    <Card
      className={cn(
        "group h-full gap-4 overflow-hidden rounded-2xl py-5 shadow-none transition-all duration-200 border-0",
        className,
      )}
    >
      <CardHeader className="px-5">
        <div className="relative aspect-[4/3] overflow-hidden rounded-lg">
          <Image
            src={post.coverImage || post.image || '/images/placeholder.jpg'}
            alt={post.title}
            fill
            unoptimized
            className="object-cover transition-all duration-300 group-hover:scale-105"
          />
        </div>
      </CardHeader>
      <CardContent className="mb-4 px-5">
        <h2 className="text-xl font-medium">{post.title}</h2>
        <p className="text-muted-foreground mt-2 line-clamp-2">
          {post.description}
        </p>
      </CardContent>
      <CardFooter className="mt-auto px-5 flex items-center justify-between gap-2">
        <span className="text-foreground text-sm font-medium group-hover:underline">
          Explore the series
        </span>
        {shouldShowBuyPrint() && (
          <span className="text-xs font-medium text-muted-foreground">
            Buy Print
          </span>
        )}
      </CardFooter>
    </Card>
  );
}
